import { FormEvent, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import UiIcon from '../components/common/UiIcon';
import { getAuthSession, loginInternalReview } from '../lib/auth';

const INTERNAL_REVIEW_EMAIL = 'review@myoan.local';

export default function InternalReviewLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState(INTERNAL_REVIEW_EMAIL);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const nextPath = new URLSearchParams(location.search).get('next') || '/wallet/recharge';

  useEffect(() => {
    let disposed = false;

    const load = async () => {
      try {
        const session = await getAuthSession();
        if (!disposed && session?.user) {
          navigate(nextPath, { replace: true });
          return;
        }
      } finally {
        if (!disposed) {
          setChecking(false);
        }
      }
    };

    void load();
    return () => {
      disposed = true;
    };
  }, [navigate, nextPath]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (loading) return;

    setLoading(true);
    setErrorMessage('');

    try {
      await loginInternalReview(email, password);
      navigate(nextPath, { replace: true });
    } catch (error) {
      const message = error instanceof Error ? error.message : '로그인에 실패했습니다.';
      setErrorMessage(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#110d0c] px-4 py-12 text-[#f7ecde] sm:px-6 lg:px-8">
      <div className="mx-auto w-full max-w-xl rounded-[32px] border border-[#7d5528]/45 bg-[radial-gradient(circle_at_top,rgba(120,76,32,0.14),rgba(20,14,11,0.97)_68%)] p-6 shadow-[0_35px_100px_-45px_rgba(0,0,0,0.95)] sm:p-8">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-[11px] uppercase tracking-[0.34em] text-[#c89f6b]">Review Access</p>
            <h1 className="mt-3 font-serif text-3xl font-extrabold tracking-[-0.02em] text-[#fbf2e4]">
              심사용 리뷰 계정 로그인
            </h1>
          </div>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#7b5d43]/55 bg-[#211813] text-[#f2cb84] transition hover:border-[#b8842f]/70 hover:bg-[#281c16]"
            aria-label="홈으로 이동"
          >
            <UiIcon name="home" className="h-5 w-5" />
          </button>
        </div>

        <p className="mt-5 text-sm leading-7 text-[#dfc4a4]/82">
          카카오페이 심사자가 서비스 흐름을 직접 확인할 수 있도록 만든 리뷰 전용 로그인 화면입니다.
          로그인 후에는 츄르 충전, 리딩 시작, 결과 확인 과정을 일반 사용자와 같은 방식으로 확인할 수
          있습니다.
        </p>

        <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.22em] text-[#c39a68]">이메일</span>
            <input
              type="email"
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="mt-2 w-full rounded-[18px] border border-[#775634]/45 bg-[#1d1511]/90 px-4 py-3 text-sm text-[#f7ead5] outline-none transition focus:border-[#c18b3c]"
            />
          </label>

          <label className="block">
            <span className="text-xs uppercase tracking-[0.22em] text-[#c39a68]">비밀번호</span>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className="mt-2 w-full rounded-[18px] border border-[#775634]/45 bg-[#1d1511]/90 px-4 py-3 text-sm text-[#f7ead5] outline-none transition focus:border-[#c18b3c]"
            />
          </label>

          {errorMessage && (
            <div className="rounded-[18px] border border-[#7b342f]/45 bg-[#2b1513] px-4 py-3 text-sm leading-6 text-[#ffd7d2]">
              {errorMessage}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || checking}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-[#b8842f]/55 bg-[#2a1c13] px-5 py-3 text-sm font-semibold text-[#f7d48a] transition-colors hover:bg-[#322117] disabled:cursor-not-allowed disabled:opacity-60"
          >
            <UiIcon name="arrow-right" className="h-4 w-4" />
            {checking ? '세션 확인 중...' : loading ? '로그인 중...' : '리뷰 계정으로 로그인'}
          </button>
        </form>
      </div>
    </div>
  );
}
